import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema, textAreaInputSchema } from '@utils';
import { SELECT_OPTIONS_ERROR } from '../constants/errors';
import { ZodError } from 'zod';
import { ErrorSummary } from '@atoms/*';
import { TEXT_AREA_MAX_LENGTH } from '@constants';
import {
  FormTitle,
  NavigationButtons,
  RadioOptions,
  TextArea,
} from '@molecules/*';
import {
  BREACH_OF_PLANNING,
  SHORT_TERM_LETS,
  UNAUTHORISED_ADVERTISEMENTS,
  UNAUTHORISED_CHANGE_OF_USE,
  UNAUTHORISED_INSTALLATION,
  UNAUTHORISED_WORKS,
} from '../constants/constants';

export const AnyMoreInfo: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, update, resetSelective } = usePlanningBreachStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Do you have any other information about this issue?';
  const moreDetailsLabelText = 'Give more details ';
  const moreDetailsDescription = `For example if you've spoken to your local councillor about the issue.`;

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: data.anyMoreInfo === 'Yes',
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: data.anyMoreInfo === 'No',
    },
  ];

  const handleChangeOptions = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No';

    if (updatedSelection) {
      update('anyMoreInfo', updatedSelection);
    } else {
      resetSelective();
    }
  };

  const handleChangeDetails = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    update('detailsOfAnyMoreInfo', event.currentTarget.value);
    setErrorMessages([]);
  };

  const handleNavigate = () => {
    switch (data.issueType) {
      case UNAUTHORISED_ADVERTISEMENTS:
        navigate(RoutesList.AD_PROBLEM);
        break;
      case UNAUTHORISED_CHANGE_OF_USE:
        navigate(RoutesList.CHANGE_PROBLEM);
        break;
      case UNAUTHORISED_INSTALLATION:
        navigate(RoutesList.INSTALLATION_PROBLEM);
        break;
      case UNAUTHORISED_WORKS:
        navigate(RoutesList.WORKS_PROBLEM);
        break;
      case BREACH_OF_PLANNING:
        navigate(RoutesList.BREACH_PROBLEM);
        break;
      case SHORT_TERM_LETS:
        navigate(RoutesList.SHORT_TERM_LETS_PROBLEM);
      default:
        return false;
    }
  };

  const buttonPrevious = {
    onClick: () => {
      handleNavigate();
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'anyMoreInfo',
          SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          anyMoreInfo: data.anyMoreInfo,
        });
        if (data.anyMoreInfo === 'Yes') {
          textAreaInputSchema({
            fieldName: 'detailsOfAnyMoreInfo',
            requiredMessage: 'Enter additional information about the issue.',
            maxLength: TEXT_AREA_MAX_LENGTH,
            maxLengthMessage: `Additional information must be ${TEXT_AREA_MAX_LENGTH} characters or less.`,
          }).parse({
            detailsOfAnyMoreInfo: data.detailsOfAnyMoreInfo,
          });
        }
        navigate(RoutesList.EMAIL_CONFIRMATION_REPORT);
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        console.log({ validationErrors });
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            ...error,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    <>
      <div className={' mt-6'}>
        <ErrorSummary messages={errorMessages} />
      </div>
      <FormTitle text={title} />
      <RadioOptions
        options={options}
        onChange={handleChangeOptions}
        errorPath={'anyMoreInfo'}
        errorMessages={errorMessages}
      />
      {data.anyMoreInfo === 'Yes' ? (
        <TextArea
          labelText={moreDetailsLabelText}
          isLabelTextTitle={false}
          description={moreDetailsDescription}
          value={data.detailsOfAnyMoreInfo}
          onChange={handleChangeDetails}
          errorPath={'detailsOfAnyMoreInfo'}
          errorMessages={errorMessages}
        />
      ) : null}
      <NavigationButtons
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
      />
    </>
  );
};
